<script setup lang="ts">
import { v4 as uuid } from "uuid";
import { computed, inject, ref, useSlots, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { Action } from "@/action/types";
import { shapeActionMenuItemClicked } from "@/analytics/events/draw";
import { stickyNoteActionMenuItemClicked } from "@/analytics/events/sticky";
import { trackEvent } from "@/analytics/track";
import { cardKey } from "@/components/StickyNote/injectKeys";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import type { DropdownMenuProps } from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Icon } from "@/model/icon";

import { shapeKey } from "../injectKeys";

const card = inject(cardKey, undefined);
const shape = inject(shapeKey, undefined);

defineOptions({ inheritAttrs: false });

interface Props {
  action: Action<never[]>;
  tooltip?: string;
  icon?: Icon;
  ariaLabel?: string;
  active?: boolean;
  disabled?: boolean;
  width?: DropdownMenuProps["width"];
  maxHeight?: DropdownMenuProps["maxHeight"];
  disableTeleport?: DropdownMenuProps["disableTeleport"];
}

const props = withDefaults(defineProps<Props>(), {
  tooltip: undefined,
  icon: undefined,
  ariaLabel: undefined,
  active: undefined,
  disabled: undefined,
  width: undefined,
  maxHeight: undefined,
  disableTeleport: true,
});

const emit = defineEmits<{ open: [] }>();

const { t } = useI18n();
const slots = useSlots();

const dropdownId = uuid();
const isDropdownOpen = ref(false);

function onDropdownOpen() {
  isDropdownOpen.value = true;
  emit("open");
}

const isActive = computed(() => props.active || (isDropdownOpen.value && slots.dropdown));
const tooltipText = computed(() => props.tooltip || t(props.action.data.name));
const ariaText = computed(() => props.ariaLabel || t(props.action.data.label));
const triggerButtonRef = useTemplateRef("triggerButton");

watch(isDropdownOpen, (newVal: boolean) => {
  if (!newVal) {
    triggerButtonRef.value?.focus();
  }
});

const handleClick = () => {
  // don't count events when the dropdown is open
  if (isDropdownOpen.value) return;

  const actionId = props.action.data.id.replaceAll(".", "_");
  if (card) {
    trackEvent(stickyNoteActionMenuItemClicked(actionId, card.type.functionality));
  } else if (shape) {
    trackEvent(shapeActionMenuItemClicked(actionId));
  }
};
</script>

<template>
  <DropdownMenu
    class="action-menu-dropdown"
    stay-open-on-content-click
    :disable-teleport
    :disabled
    :width="width || 'md'"
    :max-height="maxHeight || 'md'"
    @open="onDropdownOpen"
    @close="isDropdownOpen = false"
  >
    <template #trigger>
      <BaseTooltip :delay="[500, 0]" :offset="[0, 8]" class="tooltip">
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
        <!--        TODO use actionbutton?-->
        <button
          ref="triggerButton"
          :class="['content', { active: isActive }]"
          :aria-label="ariaText"
          :aria-controls="dropdownId"
          :aria-expanded="isDropdownOpen"
          :disabled
          v-bind="$attrs"
          @click="handleClick"
        >
          <slot name="content">
            <SvgIcon
              :name="icon || action.data.icon || 'system/placeholder'"
              width="20"
              height="20"
            />
          </slot>
        </button>
        <div class="dropdown-icon">
          <svg
            width="6"
            height="6"
            viewBox="0 0 6 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 0.5C5 0.223858 5.22386 0 5.5 0C5.77614 0 6 0.223858 6 0.5V5.5C6 5.77614 5.77614 6 5.5 6H0.5C0.223858 6 0 5.77614 0 5.5C0 5.22386 0.223858 5 0.5 5H5V0.5Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <template v-if="tooltipText" #content>{{ tooltipText }}</template>
      </BaseTooltip>
    </template>

    <div :id="dropdownId" class="wrapper">
      <slot />
    </div>
  </DropdownMenu>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";
@use "./action-menu";

.action-menu-dropdown {
  position: relative;
  height: 36px;
  width: 36px;

  &:not(:last-child) {
    border-right: 1px solid colors.$palette-white-alpha-10;
  }

  .tooltip {
    display: inline-block;
    height: 100%;
    width: 100%;
  }

  .content {
    @include action-menu.action-menu;

    padding: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &.active {
      background-color: colors.$palette-white-alpha-20;
      color: colors.$icon-inverse;
    }
  }

  .dropdown-icon {
    pointer-events: none;
    display: grid;
    place-items: center;
    position: absolute;
    bottom: 4px;
    right: 4px;
    color: colors.$icon-subtle-inverse;
  }

  .content:hover + .dropdown-icon,
  .content:active + .dropdown-icon,
  .content.active + .dropdown-icon {
    color: colors.$icon-inverse;
  }

  .wrapper {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
}
</style>
